import ChatbotApi from './ChatbotApi';
import ChatbotState from './ChatbotState';
import TConversation from './TConversation';
import {isPermanentHttpError} from './ChatbotRequestError';

export default class ChatbotErrorRecovery {

    private static readonly RETRY_INTERVAL_MS = 5000;
    private static readonly REQUEST_TIMEOUT_MS = 10000;

    private destroyed = false;
    private retryTimer?: ReturnType<typeof setTimeout>;
    private finishRetryWait?: () => void;

    constructor(
        private readonly api: ChatbotApi,
        private readonly state: ChatbotState,
        private readonly applyConversation: (conversation: TConversation) => void,
        private readonly onUpdate: () => void,
    ) {}

    public async recover(expectedMessageCount: number): Promise<void> {
        if (this.destroyed || this.state.checkingStatus) {
            return;
        }
        this.state.checkingStatus = true;
        while (!this.destroyed) {
            const status = await this.checkConversation(expectedMessageCount);
            if (this.destroyed || status === 'finished') {
                break;
            }
            this.state.recovering = true;
            this.onUpdate();
            await this.waitForRetry();
        }
        this.stopRecovery();
        this.state.checkingStatus = false;
        this.onUpdate();
    }

    private async checkConversation(expectedMessageCount: number): Promise<'pending' | 'finished'> {
        try {
            const conversation = await this.api.getConversation(ChatbotErrorRecovery.REQUEST_TIMEOUT_MS);
            if (this.destroyed) {
                return 'finished';
            }
            this.applyConversation(conversation);
            if (this.state.locked || this.state.hasError || this.hasCompletedAnswer(expectedMessageCount)) {
                return 'finished';
            }
        } catch (error) {
            console.error(error);
            if (isPermanentHttpError(error)) {
                this.state.locked = true;
                this.state.hasError = true;
                return 'finished';
            }
        }
        return 'pending';
    }

    private hasCompletedAnswer(expectedMessageCount: number): boolean {
        const lastMessage = this.state.messages[this.state.messages.length - 1];
        return this.state.messages.length >= expectedMessageCount
            && lastMessage?.role === 'assistant' && lastMessage.completed;
    }

    private waitForRetry(): Promise<void> {
        return new Promise(resolve => {
            this.finishRetryWait = () => {
                this.retryTimer = undefined;
                this.finishRetryWait = undefined;
                resolve();
            };
            this.retryTimer = setTimeout(this.finishRetryWait, ChatbotErrorRecovery.RETRY_INTERVAL_MS);
        });
    }

    private stopRecovery(): void {
        clearTimeout(this.retryTimer);
        this.finishRetryWait?.();
        this.retryTimer = undefined;
        this.state.recovering = false;
    }

    public destroy(): void {
        this.destroyed = true;
        this.stopRecovery();
    }
}
